import { useState } from 'react'
import { useTemplates, useSendNotification } from '@/hooks/useNotificacoes'
import { X, Loader2, Send, CheckCircle2, Smartphone, Globe, Mail } from 'lucide-react'
import type { UUID } from '@/types/database'
import { useAuthStore } from '@/stores/auth.store'

interface SendNotificationFormProps {
    onClose: () => void
}

export function SendNotificationForm({ onClose }: SendNotificationFormProps) {
    const authUser = useAuthStore(s => s.user)
    const { data: templates, isLoading: loadingTemplates } = useTemplates()
    const sendMut = useSendNotification()

    const [templateId, setTemplateId] = useState('')
    const [segmentacao, setSegmentacao] = useState<'todos' | 'ativos' | 'vencidos'>('todos')
    const [success, setSuccess] = useState(false)

    const selectedTemplate = templates?.find(t => t.id === templateId)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!templateId) return

        try {
            await sendMut.mutateAsync({
                template_id: templateId as UUID,
                titulo: selectedTemplate?.assunto || selectedTemplate?.nome,
                corpo: selectedTemplate?.conteudo || '',
                canal: selectedTemplate?.canal,
                tenant_id: authUser?.tenant_id,
                segmentacao
            } as any)

            setSuccess(true)
            setTimeout(onClose, 2000)
        } catch (err) {
            console.error(err)
            alert('Erro ao processar envio')
        }
    }

    if (success) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                <div className="bg-white w-full max-w-md p-12 text-center animate-scale-in rounded-3xl shadow-elevated">
                    <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 size={40} />
                    </div>
                    <h3 className="text-2xl font-bold text-surface-900 mb-2">Envio Iniciado!</h3>
                    <p className="text-surface-600">As mensagens estão sendo processadas e enviadas em segundo plano.</p>
                </div>
            </div>
        )
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
            <div className="bg-white w-full max-w-lg flex flex-col overflow-hidden rounded-3xl shadow-elevated">
                <div className="p-4 border-b border-surface-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-surface-900 flex items-center gap-2">
                        <Send size={18} className="text-primary-600" /> Novo Envio Manual
                    </h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-200 text-surface-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div>
                        <label className="label">1. Escolha o Template</label>
                        {loadingTemplates ? (
                            <div className="flex items-center gap-2 text-surface-400 py-2">
                                <Loader2 size={16} className="animate-spin" />
                                <span className="text-xs">Carregando modelos...</span>
                            </div>
                        ) : (
                            <select
                                required value={templateId} onChange={e => setTemplateId(e.target.value)}
                                className="input"
                            >
                                <option value="">Selecione um template...</option>
                                {templates?.filter(t => t.ativo).map(t => (
                                    <option key={t.id} value={t.id}>{t.nome} ({t.canal})</option>
                                ))}
                            </select>
                        )}
                        {selectedTemplate && (
                            <div className="mt-3 p-4 rounded-xl border border-surface-100 text-[11px] text-surface-600 animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="flex justify-between items-center mb-3">
                                    <p className="font-bold uppercase text-surface-400 tracking-wider">Prévia do Conteúdo</p>
                                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-[10px] font-bold uppercase
                                        ${selectedTemplate.canal === 'whatsapp' ? 'bg-green-50 border-green-100 text-green-600' :
                                            selectedTemplate.canal === 'web_push' ? 'bg-primary-50 border-primary-100 text-primary-600' :
                                                'bg-blue-50 border-blue-100 text-blue-600'}`}>
                                        {selectedTemplate.canal === 'whatsapp' ? <Smartphone size={12} /> :
                                            selectedTemplate.canal === 'web_push' ? <Globe size={12} /> :
                                                <Mail size={12} />}
                                        {selectedTemplate.canal.replace('_', ' ')}
                                    </div>
                                </div>
                                <div className="bg-white p-3 rounded-lg border border-surface-100/50 shadow-sm italic text-surface-700/80 leading-relaxed">
                                    <p className="font-mono whitespace-pre-wrap">{selectedTemplate.conteudo}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="label">2. Público-Alvo (Segmentação)</label>
                        <div className="grid grid-cols-1 gap-2">
                            {[
                                { id: 'todos', label: 'Todos os Associados', desc: 'Envia para toda a base de dados' },
                                { id: 'ativos', label: 'Somente Ativos', desc: 'Contratos com status Ativo ou Vigente' },
                                { id: 'vencidos', label: 'Inadimplentes', desc: 'Associados com faturas vencidas' }
                            ].map(s => (
                                <button
                                    key={s.id} type="button"
                                    onClick={() => setSegmentacao(s.id as any)}
                                    className={`flex items-start gap-3 p-3 rounded-xl border transition-all text-left
                                        ${segmentacao === s.id ? 'bg-primary-50 border-primary-200 ring-1 ring-primary-200' : 'bg-white border-surface-100 hover:bg-surface-50 text-surface-600'}`}
                                >
                                    <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0
                                        ${segmentacao === s.id ? 'border-primary-600 bg-primary-600' : 'border-surface-300'}`}>
                                        {segmentacao === s.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                    </div>
                                    <div>
                                        <p className={`font-bold text-sm ${segmentacao === s.id ? 'text-primary-900' : 'text-surface-900'}`}>{s.label}</p>
                                        <p className="text-[10px] opacity-60 font-medium">{s.desc}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="pt-2">
                        <button type="submit" disabled={!templateId || sendMut.isPending}
                            className="btn btn-primary w-full gap-2 h-12 shadow-lg shadow-primary-500/20">
                            {sendMut.isPending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                            Disparar Mensagens Agora
                        </button>
                        <p className="text-[10px] text-center text-surface-400 mt-3 font-medium">
                            O envio será processado em lotes para evitar bloqueios.
                        </p>
                    </div>
                </form>
            </div>
        </div>
    )
}
